/**
 * ChatService constants: provider options, API key help links, model options, and copy.
 */
import type { ChatServiceProviderId, ChatServiceProviderOption } from './types';

/** Model option for dropdown: label (display) and value (API value) */
export interface ModelOption {
  label: string;
  value: string;
}

/** ChatGPT / OpenAI model options: Model Name (label) and API Model ID (value) */
export const CHATGPT_MODELS: ModelOption[] = [
  { label: 'GPT-5.2 (Flagship)', value: 'gpt-5.2' },
  { label: 'GPT-5 Mini', value: 'gpt-5-mini' },
  { label: 'GPT-4.1 (Stable)', value: 'gpt-4.1' },
  { label: 'GPT-4.1 Mini', value: 'gpt-4.1-mini' },
  { label: 'GPT-4o (Omni)', value: 'gpt-4o' },
  { label: 'GPT-4 Turbo', value: 'gpt-4-turbo' },
  { label: 'o4-mini / o3', value: 'o4-mini, o3' },
];

/** Azure OpenAI model options: Model Name (label) and API Deployment ID (value) */
export const AZURE_OPENAI_MODELS: ModelOption[] = [
  { label: 'GPT-5.2 Chat', value: 'gpt-52-chat' },
  { label: 'GPT-4.1', value: 'gpt-41-stable' },
  { label: 'GPT-4.1 Mini', value: 'gpt-41-mini' },
  { label: 'GPT-4o', value: 'gpt-4o-2024-11-20' },
  { label: 'o1-pro', value: 'o1-pro' },
];

/** Gemini model options: Model Name (label) and API Model ID (value) */
export const GEMINI_MODELS: ModelOption[] = [
  { label: 'Gemini 3 Pro', value: 'gemini-3-pro-preview' },
  { label: 'Gemini 3 Flash', value: 'gemini-3-flash-preview' },
  { label: 'Gemini 2.5 Pro', value: 'gemini-2.5-pro' },
  { label: 'Gemini 2.5 Flash', value: 'gemini-2.5-flash' },
  { label: 'Gemini 2.0 Flash', value: 'gemini-2.0-flash-001' },
  { label: 'Gemini 2.0 Lite', value: 'gemini-2.0-flash-lite-001' },
];

/** Embedding model options for ChatGPT and Gemini (label and API value) */
export const EMBEDDING_MODEL_OPTIONS: ModelOption[] = [
  { label: 'text-embedding-ada-002', value: 'text-embedding-ada-002' },
  { label: 'text-embedding-3-small', value: 'text-embedding-3-small' },
];

/** Get model options for the given provider */
export function getModelOptions(provider: ChatServiceProviderId): ModelOption[] {
  switch (provider) {
    case 'ChatGPT':
      return CHATGPT_MODELS;
    case 'AzureOpenAI':
      return AZURE_OPENAI_MODELS;
    case 'Gemini':
      return GEMINI_MODELS;
    default:
      return [];
  }
}

/** Provider options for the selection cards (ChatGPT, Azure OpenAI, Gemini); icons from public/images/apps */
export const CHAT_SERVICE_PROVIDERS: ChatServiceProviderOption[] = [
  { id: 'ChatGPT', label: 'ChatGPT', imageUrl: '/images/apps/chatgpt.png' },
  { id: 'AzureOpenAI', label: 'Azure OpenAI', imageUrl: '/images/apps/openai.png' },
  { id: 'Gemini', label: 'Gemini', imageUrl: '/images/apps/gemini.png' },
];

/** Get provider icon URL from token_type (e.g. "azureopenai" -> Azure OpenAI icon) */
export function getProviderImageUrl(tokenType: string | undefined): string {
  const key = (tokenType ?? '').toLowerCase().replace(/\s/g, '');
  const provider = CHAT_SERVICE_PROVIDERS.find((p) => p.id.toLowerCase().replace(/\s/g, '') === key);
  return provider?.imageUrl ?? '/images/apps/openai.png';
}

/** Get provider display name from token_type (e.g. "azureopenai" -> "Azure OpenAI") */
export function getProviderLabel(tokenType: string | undefined): string {
  const key = (tokenType ?? '').toLowerCase().replace(/\s/g, '');
  const provider = CHAT_SERVICE_PROVIDERS.find((p) => p.id.toLowerCase().replace(/\s/g, '') === key);
  return provider?.label ?? (tokenType ?? '—');
}

/** All model option arrays for lookup by value (used to get label from id) */
const ALL_MODEL_OPTIONS = [...CHATGPT_MODELS, ...AZURE_OPENAI_MODELS, ...GEMINI_MODELS];

/** Get model display name from model ID (e.g. "gpt-4o-2024-11-20" -> "GPT-4o") */
export function getModelLabel(modelId: string | undefined): string {
  if (!modelId) return '—';
  const option = ALL_MODEL_OPTIONS.find((m) => m.value === modelId);
  return option?.label ?? modelId;
}

/** Help URL for generating OpenAI API key */
export const OPENAI_API_KEY_HELP_URL =
  'https://platform.openai.com/api-keys';

/** Help URL for Azure OpenAI (user-defined from Azure Console) */
export const AZURE_OPENAI_HELP_URL =
  'https://learn.microsoft.com/en-us/azure/ai-services/openai/';

/** Help URL for Gemini API key */
export const GEMINI_API_KEY_HELP_URL =
  'https://ai.google.dev/gemini-api/docs/api-key';

/** Get help URL by provider */
export function getApiKeyHelpUrl(provider: ChatServiceProviderId): string {
  switch (provider) {
    case 'ChatGPT':
      return OPENAI_API_KEY_HELP_URL;
    case 'AzureOpenAI':
      return AZURE_OPENAI_HELP_URL;
    case 'Gemini':
      return GEMINI_API_KEY_HELP_URL;
    default:
      return OPENAI_API_KEY_HELP_URL;
  }
}

/** Modal title by provider */
export function getModalTitle(provider: ChatServiceProviderId): string {
  switch (provider) {
    case 'ChatGPT':
      return 'Chat GPT Setup';
    case 'AzureOpenAI':
      return 'Azure OpenAI Setup';
    case 'Gemini':
      return 'Gemini Setup';
    default:
      return 'Chat Service Setup';
  }
}

/** LLM type string sent to validation API (always lowercase) */
export function getLlmTypeForValidation(provider: ChatServiceProviderId): string {
  switch (provider) {
    case 'ChatGPT':
      return 'chatgpt';
    case 'AzureOpenAI':
      return 'azureopenai';
    case 'Gemini':
      return 'gemini';
    default:
      return String(provider).toLowerCase();
  }
}

/** token_type for save/update API */
export function getTokenTypeForApi(provider: ChatServiceProviderId): string {
  return getLlmTypeForValidation(provider);
}
